import React from 'react';
import {
  Button,
  Typography,
  useTheme,
  Box,
  Grid,
  Stack,
  Paper,
  Chip,
} from '@mui/material';
import startCase from 'lodash/startCase';
import moment from 'moment';
import { moneyPrint } from '@pv/common/utils';
import { ExternalLink, Calendar, DollarSign, Clock, Map } from 'react-feather';
import type { AdminVenueQuery } from '@pv/common/graphql/operations';

type AddOnPlan = AdminVenueQuery['adminVenue']['addOnPlans'][number];

const formatDate = (date?: string | null) => {
  if (!date) return '';
  return moment(date).format('MMMM DD, YYYY');
};

const ADDON_ICON_MAP: Record<string, React.ElementType> = {
  floor_plans: Map,
};

export const AddOnPlanItem = ({ addOnPlan }: { addOnPlan: AddOnPlan }) => {
  const theme = useTheme();
  const { addOnDefinition, stripeSubscriptionId } = addOnPlan;
  const AddOnIcon = ADDON_ICON_MAP[addOnDefinition.slug] ?? DollarSign;

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'success';
      case 'canceled':
      case 'cancelled':
        return 'error';
      case 'past_due':
        return 'warning';
      case 'trialing':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 2,
      }}
    >
      <Stack spacing={1}>
        <Box sx={{ p: 3 }}>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            gutterBottom
            sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
          >
            ADD-ON
          </Typography>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            flexWrap="wrap"
            sx={{ mt: 0.5 }}
          >
            <AddOnIcon size={20} style={{ flexShrink: 0, opacity: 0.8 }} />
            <Typography variant="h6" fontWeight={600}>
              {addOnDefinition.name}
            </Typography>
            <Chip
              label={startCase(addOnPlan.status || '')}
              color={getStatusColor(addOnPlan.status || '') as any}
              size="small"
              sx={{ fontWeight: 500 }}
            />
            {addOnPlan.trialing && addOnPlan.trialEndsAt && (
              <Typography variant="body2" color="text.secondary">
                Trial ends {formatDate(addOnPlan.trialEndsAt)}
              </Typography>
            )}
            {addOnPlan.trialExpired && !addOnPlan.active && (
              <Chip label="Trial expired" size="small" variant="outlined" />
            )}
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {/* Billing */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              BILLING
            </Typography>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              <Typography variant="body1">
                {moneyPrint(addOnPlan.effectiveMonthlyRate / 100)} / month
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {moneyPrint(addOnPlan.effectiveAnnualRate / 100)} annually
              </Typography>
              {addOnPlan.discount > 0 && (
                <Typography variant="body2" color="success.main">
                  {Math.round(addOnPlan.discount * 100)}% discount applied
                </Typography>
              )}
            </Stack>
          </Grid>

          {/* Timeline */}
          <Grid item xs={12} md={6}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              gutterBottom
              sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}
            >
              TIMELINE
            </Typography>
            <Stack spacing={1} sx={{ mt: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Calendar size={16} />
                <Typography variant="body2">
                  <strong>Start:</strong>{' '}
                  {addOnPlan.startDate ? formatDate(addOnPlan.startDate) : '—'}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Clock size={16} />
                <Typography variant="body2">
                  <strong>End:</strong>{' '}
                  {addOnPlan.endDate
                    ? formatDate(addOnPlan.endDate)
                    : 'Ongoing'}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {stripeSubscriptionId && (
          <Box
            sx={{
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Button
              size="medium"
              variant="outlined"
              target="_blank"
              rel="noopener noreferrer"
              href={`https://dashboard.stripe.com/subscriptions/${stripeSubscriptionId}`}
              startIcon={<ExternalLink size={16} />}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              View in Stripe Dashboard
            </Button>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
